/**
 * Flow Selector Component
 * Allows users to choose an industry-specific guided flow template
 */

import React from 'react';
import { Scale, DollarSign, Shield, Zap, ArrowRight } from 'lucide-react';
import { getAllFlowPresets } from './industryFlows';
import type { FlowPreset } from './types';

interface FlowSelectorProps {
  onSelectFlow: (preset: FlowPreset) => void;
  onCancel: () => void;
}

const INDUSTRY_ICONS = {
  legal: Scale,
  finance: DollarSign,
  ai_governance: Shield,
  infrastructure: Zap
};

const INDUSTRY_COLORS = {
  legal: 'from-amber-500 to-orange-600',
  finance: 'from-emerald-500 to-green-600',
  ai_governance: 'from-cyan-500 to-blue-600',
  infrastructure: 'from-purple-500 to-pink-600'
};

export const FlowSelector: React.FC<FlowSelectorProps> = ({ onSelectFlow, onCancel }) => {
  const flows = getAllFlowPresets();

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 p-8">
      <div className="max-w-6xl mx-auto">
        <div className="mb-8">
          <button
            onClick={onCancel}
            className="text-gray-400 hover:text-white transition-colors mb-4"
          >
            ← Back to Console
          </button>
          <h1 className="text-4xl font-bold text-white mb-3">
            Select Guided Flow Template
          </h1>
          <p className="text-gray-300 text-lg">
            Choose an industry-specific template to construct your governed intent with detailed guidance.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {flows.map((flow) => {
            const Icon = INDUSTRY_ICONS[flow.industry as keyof typeof INDUSTRY_ICONS] || Shield;
            const gradient = INDUSTRY_COLORS[flow.industry as keyof typeof INDUSTRY_COLORS] || 'from-gray-500 to-gray-600';

            return (
              <div
                key={flow.id}
                onClick={() => onSelectFlow(flow)}
                className="group relative bg-gray-800/50 border border-gray-700 rounded-xl p-6 hover:border-cyan-500 transition-all cursor-pointer hover:shadow-xl hover:shadow-cyan-500/20"
              >
                {/* Icon Badge */}
                <div className={`absolute -top-4 -left-4 w-16 h-16 bg-gradient-to-br ${gradient} rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform`}>
                  <Icon className="w-8 h-8 text-white" />
                </div>

                {/* Content */}
                <div className="pl-16">
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="text-xl font-bold text-white group-hover:text-cyan-400 transition-colors">
                      {flow.name}
                    </h3>
                    <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-cyan-400 group-hover:translate-x-1 transition-all" />
                  </div>

                  <p className="text-gray-300 text-sm mb-4 leading-relaxed">
                    {flow.description}
                  </p>

                  {/* Metadata */}
                  <div className="flex flex-wrap gap-2">
                    <span className="px-3 py-1 bg-gray-700/50 text-gray-300 text-xs rounded-full">
                      {flow.steps.length} steps
                    </span>
                    <span className="px-3 py-1 bg-cyan-500/20 text-cyan-400 text-xs rounded-full">
                      {flow.category}
                    </span>
                    <span className="px-3 py-1 bg-purple-500/20 text-purple-400 text-xs rounded-full">
                      {flow.auditClass}
                    </span>
                  </div>

                  {/* Step Preview */}
                  <div className="mt-4 pt-4 border-t border-gray-700">
                    <p className="text-gray-400 text-xs mb-2 font-semibold">STEPS INCLUDED:</p>
                    <div className="space-y-1">
                      {flow.steps.slice(0, 3).map((step, idx) => (
                        <div key={step.id} className="flex items-center gap-2 text-gray-500 text-xs">
                          <span className="w-5 h-5 flex items-center justify-center bg-gray-700 rounded-full text-gray-400 font-semibold">
                            {idx + 1}
                          </span>
                          <span>{step.label}</span>
                        </div>
                      ))}
                      {flow.steps.length > 3 && (
                        <div className="text-gray-500 text-xs pl-7">
                          +{flow.steps.length - 3} more steps...
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Info Footer */}
        <div className="mt-8 p-6 bg-gray-800/30 border border-gray-700 rounded-xl">
          <h4 className="text-white font-semibold mb-2">📋 About Guided Flows</h4>
          <p className="text-gray-400 text-sm leading-relaxed">
            Each template guides you through industry-specific terminology and requirements, ensuring your request is 
            properly structured for authority review. Templates include inline validation, examples, and educational 
            context derived from the <strong className="text-cyan-400">Intent Model: Cross-Industry Verification Framework</strong>.
          </p>
        </div>
      </div>
    </div>
  );
};
